import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PageBanner from '../../components/ui/PageBanner';
import SectionHeading from '../../components/ui/SectionHeading';
import Button from '../../components/ui/Button';
import contactezNous from '../../assets/images/ContactezNous.webp';
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  UserGroupIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface Location {
  id: string;
  title: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  address: string;
  phone: string;
  email: string;
  schedule: string;
  color: string;
}

const locations: Location[] = [
  {
    id: 'maternelle',
    title: 'Maternelle',
    icon: AcademicCapIcon,
    address: 'N°55, Rue 27, Oulfa, Casablanca',
    phone: '05 22 89 52 24',
    email: 'maternelle@gssanaa.com',
    schedule: '8h30 - 16h30',
    color: 'bg-pink-100'
  },
  {
    id: 'primaire',
    title: 'Primaire',
    icon: BookOpenIcon,
    address: '362 Bd Ouad Oum Rabii, Oulfa',
    phone: '05 22 90 82 38',
    email: 'primaire@gssanaa.com',
    schedule: '8h30 - 16h30',
    color: 'bg-blue-100'
  },
  {
    id: 'college',
    title: 'Collège',
    icon: BuildingOfficeIcon,
    address: 'N°31, Rue 27, Oulfa, Casablanca',
    phone: '05 22 89 52 40',
    email: 'college@gssanaa.com',
    schedule: '8h30 - 16h30',
    color: 'bg-green-100'
  },
  {
    id: 'lycee',
    title: 'Lycée',
    icon: UserGroupIcon,
    address: 'N°5 et 7, Angle Rue 49 et 50 Groupe "U", Oulfa, Casablanca',
    phone: '05 22 93 04 08',
    email: 'lycee@gssanaa.com',
    schedule: '8h30 - 16h30',
    color: 'bg-purple-100'
  },
];

const ContactPage = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real implementation, this would send the form data to a server
    console.log('Form data submitted:', formData);
    setFormSubmitted(true);

    // Reset form after submission
    setTimeout(() => {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
      setFormSubmitted(false);
    }, 5000);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <PageBanner
        title="Contactez-nous"
        subtitle="Nous sommes à votre écoute"
        imageSrc={contactezNous}
      />

      {/* Contact Information Cards */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeading
            title="Nos coordonnées"
            subtitle="Trouvez-nous facilement"
            centered={true}
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {locations.map((location, index) => (
              <motion.div
                key={location.id}
                className={`rounded-lg shadow-elegant p-6 ${location.color}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                    <location.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="ml-3 text-lg font-semibold">{location.title}</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-start">
                    <MapPinIcon className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">{location.address}</span>
                  </div>
                  <div className="flex items-start">
                    <PhoneIcon className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">{location.phone}</span>
                  </div>
                  <div className="flex items-start">
                    <EnvelopeIcon className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <a href={`mailto:${location.email}`} className="text-gray-700 hover:text-primary">
                      {location.email}
                    </a>
                  </div>
                  <div className="flex items-start">
                    <ClockIcon className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <div>
                      <span className="text-gray-700">{location.schedule}</span>
                      {location.id === 'lycee' && (
                        <span className="block text-xs text-primary-dark font-semibold mt-0.5">
                          Horaire continu à partir de 2026-2027
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WhatsApp Quick Contact */}
      <section className="py-10 bg-green-50 border-y border-green-100">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <ChatBubbleLeftRightIcon className="h-7 w-7 text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-lg">WhatsApp — réponse instantanée</p>
                <p className="text-gray-600 text-sm">Notre agent IA répond à vos questions sur les inscriptions, le programme et les frais de scolarité, 7j/7.</p>
              </div>
            </div>
            <a
              href="https://wa.me/212781570080"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex-shrink-0"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
              </svg>
              Écrire sur WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <SectionHeading
                title="Envoyez-nous un message"
                subtitle="Comment pouvons-nous vous aider ?"
              />
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Vous avez des questions sur nos programmes, nos frais de scolarité ou notre processus d'admission ? N'hésitez pas à nous contacter en remplissant le formulaire ci-dessous. Notre équipe vous répondra dans les plus brefs délais.
              </p>
              
              {formSubmitted ? (
                <motion.div
                  className="bg-green-50 border-l-4 border-green-500 p-4 mb-8"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="text-green-700">
                    Votre message a été envoyé avec succès. Nous vous répondrons dès que possible.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Nom *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                      Sujet *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    ></textarea>
                  </div>
                  
                  <div>
                    <Button type="submit" variant="primary" size="large">
                      Envoyer le message
                    </Button>
                  </div>
                </form>
              )}
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="sticky top-24">
                <div className="bg-white p-6 rounded-lg shadow-elegant mb-8">
                  <h3 className="text-xl font-semibold mb-4 border-b border-gray-200 pb-2">Horaires par cycle</h3>
                  <ul className="space-y-3">
                    <li className="flex justify-between">
                      <span className="text-gray-700">Maternelle → Collège :</span>
                      <span className="font-medium">8h30 - 16h30</span>
                    </li>
                    <li>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Lycée :</span>
                        <span className="font-medium">8h30 - 16h30</span>
                      </div>
                      <div className="text-xs text-primary-dark font-semibold mt-0.5">
                        Horaire continu dès 2026-2027
                      </div>
                    </li>
                    <li className="flex justify-between border-t border-gray-100 pt-3">
                      <span className="text-gray-700">Samedi :</span>
                      <span className="font-medium">Fermé</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-gray-700">Dimanche :</span>
                      <span className="font-medium">Fermé</span>
                    </li>
                  </ul>
                </div>
                
                <div className="h-80 bg-gray-200 rounded-lg overflow-hidden">
                   <iframe 
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3324.833038838069!2d-7.683347011334203!3d33.55771403247783!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xda62cb35a9423f9%3A0x6d9a6ffc6d2b8ce0!2sGroupe%20Scolaire%20Sanaa!5e0!3m2!1sfr!2sma!4v1747925850595!5m2!1sfr!2sma"
                      width="100%" 
                      height="100%" 
                      style={{ border: 0 }} 
                      allowFullScreen={true}
                      loading="lazy" 
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Groupe Scolaire Sanaa Location"
                    ></iframe>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeading
            title="Questions fréquentes"
            subtitle="Besoin d'aide ?"
            centered={true}
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-xl font-semibold mb-2">Comment puis-je inscrire mon enfant à votre école ?</h3>
                <p className="text-gray-700">
                  Pour inscrire votre enfant, veuillez contacter notre service administratif ou remplir le formulaire de contact sur cette page. Nous vous fournirons toutes les informations nécessaires concernant les documents requis et la procédure à suivre.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <h3 className="text-xl font-semibold mb-2">Quels sont les frais de scolarité ?</h3>
                <p className="text-gray-700">
                  Les frais de scolarité varient selon le niveau d'enseignement. Pour obtenir des informations détaillées, veuillez contacter notre service administratif qui vous fournira un barème complet des frais pour l'année scolaire en cours.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h3 className="text-xl font-semibold mb-2">Proposez-vous un service de transport scolaire ?</h3>
                <p className="text-gray-700">
                  Oui, nous offrons un service de transport scolaire pour les élèves dans plusieurs quartiers de Casablanca. Pour connaître les itinéraires desservis et les tarifs, veuillez contacter notre administration.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <h3 className="text-xl font-semibold mb-2">Quels sont les horaires de l'école ?</h3>
                <p className="text-gray-700">
                  Les horaires varient selon le cycle. Le <strong>Lycée passe à l'horaire continu de 8h30 à 16h30 dès la rentrée 2026-2027</strong>. Un emploi du temps détaillé est fourni en début d'année scolaire.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <h3 className="text-xl font-semibold mb-2">Comment puis-je prendre rendez-vous pour visiter l'école ?</h3>
                <p className="text-gray-700">
                  Pour organiser une visite, veuillez contacter notre administration ou remplir le formulaire de contact sur cette page en précisant que vous souhaitez visiter notre établissement. Nous vous proposerons des dates disponibles pour vous accueillir.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ContactPage;
